<script setup lang="ts">
import ImagePlaceholderIcon from '@/assets/icons/Profile/ImagePlaceholderIcon.vue';
// import TheDayOffSection from '@/components/Employee/TimeClock/TheDayOffSection.vue';
import TheDayOffSection from '@/components/Employee/TimeClock/TheDayOffSection.vue';
import InlineMessage from '@/components/InlineMessage.vue';
import { useAuth } from '@/composables/useAuth';
import { useCountries } from '@/composables/useCountries';
import { useFetch } from '@/composables/useFetch';
import { useImageBase64 } from '@/composables/useImageBase64';
import { useProfileProfile } from '@/composables/useProfileProfile.js';
import { setLocale, type MessageSchema } from '@/i18n/index.js';
import { useAppStore } from '@/stores/appStore';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Password from 'primevue/password';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, reactive, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DeleteAccountDialog from './DeleteAccountDialog.vue';
import EditProfile from './EditProfile.vue';

const userStore = useUserStore();
const user = computed(() => userStore.user);
const isLoadingProfile = ref(false); // For general profile loading, including user data for DayOffSection

const { fetchData } = useFetch();

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const toast = useToast();
const { logout, isFetching } = useAuth();

const { updateUserProperty, updatePassword } = useProfileProfile(toast);
const appStore = useAppStore();

const languagesList = ref<{ label: string; value: 'fr' | 'en' | 'de' | 'it' }[]>([
  { label: 'Français', value: 'fr' },
  { label: 'English', value: 'en' },
  { label: 'Deutsch', value: 'de' },
  { label: 'Italiano', value: 'it' },
]);

const profilePhotoUrl = ref<string | undefined>('');
const fileInput: Ref<HTMLInputElement | null> = ref(null);
const isUploadingPhoto = ref(false);

const photoUrlForFetch = computed(() => user.value?.profile_picture_url ?? null);
const { imageSrc: profileImageSrc, refresh: refreshProfilePhoto } = useImageBase64(photoUrlForFetch);

const selectedLanguage = ref(languagesList.value.find((language) => language.value === appStore.language) || languagesList.value[0]);

async function updateProperty(propertyName: string, value: string | number) {
  if (!user.value) return; // Make sure the user is defined

  const updateResponse = await updateUserProperty(propertyName, value);
  console.log('Changing property', propertyName, value, updateResponse);
  if (updateResponse !== null) {
    userStore.user = updateResponse;
  }
}

function updateLanguage() {
  const newLanguage = selectedLanguage.value.value;

  appStore.setLanguage(newLanguage);
  updateProperty('language', newLanguage);
}

watch(
  () => appStore.language as 'fr' | 'en' | 'de' | 'it',
  (newLanguage) => {
    setLocale(newLanguage);
  },
);

const openChangePasswordModal = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');
const isFetchingPassword = ref(false);
const touchedPasswordFields = reactive({
  currentPassword: false,
  newPassword: false,
  confirmNewPassword: false,
});

const isSamePassword = computed(() => {
  return currentPassword.value && newPassword.value && currentPassword.value === newPassword.value;
});

const isNewPasswordValid = computed(() => {
  return !touchedPasswordFields.newPassword || newPassword.value.length >= 8;
});

const isConfirmNewPasswordValid = computed(() => {
  return !touchedPasswordFields.confirmNewPassword || newPassword.value === confirmNewPassword.value;
});

const isFormValid = computed(() => {
  return (
    (user.value?.password_set ? touchedPasswordFields.currentPassword : true) &&
    touchedPasswordFields.newPassword &&
    touchedPasswordFields.confirmNewPassword &&
    isNewPasswordValid.value &&
    isConfirmNewPasswordValid.value &&
    !isSamePassword.value
  );
});

async function submitPasswordChange() {
  isFetchingPassword.value = true;
  const success = await updatePassword(currentPassword.value, newPassword.value, confirmNewPassword.value);
  isFetchingPassword.value = false;

  if (success) {
    currentPassword.value = '';
    newPassword.value = '';
    confirmNewPassword.value = '';
    touchedPasswordFields.currentPassword = false;
    touchedPasswordFields.newPassword = false;
    touchedPasswordFields.confirmNewPassword = false;

    openChangePasswordModal.value = false;
    await logout();
  }
}

async function uploadProfilePhoto(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;

  if (!files || files.length === 0) {
    console.warn('No files selected');
    return;
  }

  if (!user.value?.id) {
    console.error('User ID not found');
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('errors.userNotFound') || 'User not found',
      life: 3000,
    });
    return;
  }

  const file: File = files[0];
  const maxFileSize = 10 * 1024 * 1024;

  // Validate file size
  if (file.size > maxFileSize) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('menuDishView.fileTooLarge', { maxSizeMB: '10' }),
      life: 3000,
    });
    // Reset file input
    if (input) input.value = '';
    return;
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];
  if (!validTypes.includes(file.type)) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('errors.invalidFileType') || 'Invalid file type. Please upload a JPEG, PNG, or WebP image.',
      life: 3000,
    });
    if (input) input.value = '';
    return;
  }

  isUploadingPhoto.value = true;
  const formData = new FormData();
  formData.append('photo', file);

  try {
    console.log('[uploadProfilePhoto] Starting upload for user:', user.value.id);
    const { data, error, statusCode } = await fetchData(`${useEnvStore().apiUrl}/users/${user.value.id}/photo`, 'POST', formData);

    if (error) {
      console.error('[uploadProfilePhoto] Upload failed:', error);

      // Extract the most specific error message
      let errorMessage = t('profile.profile.profileTab.photoUpdateFailed');

      if (error?.details?.errors?.photo?.[0]) {
        errorMessage = error.details.errors.photo[0];
      } else if (error?.details?.message) {
        errorMessage = error.details.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: errorMessage,
        life: 5000,
      });
    } else if (data && user.value) {
      console.log('[uploadProfilePhoto] Upload successful');

      // Update the user's profile picture
      user.value.profile_picture_url = data.profile_picture_url || data.url || '';
      profilePhotoUrl.value = user.value.profile_picture_url;

      // Force re-fetch of the profile photo (URL may not have changed)
      refreshProfilePhoto();

      // Refresh user data from server
      await useUserStore().refreshUser();

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('profile.profile.profileTab.photoUpdated'),
        life: 3000,
      });
    } else {
      // No data and no error - unexpected response
      console.warn('[uploadProfilePhoto] Unexpected response:', { data, error, statusCode });
      toast.add({
        severity: 'warn',
        summary: t('common.warning') || 'Warning',
        detail: t('errors.unexpectedResponse') || 'Unexpected response from server',
        life: 3000,
      });
    }
  } catch (error) {
    console.error('[uploadProfilePhoto] Unexpected error:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('profile.profile.profileTab.photoUpdateFailed'),
      life: 5000,
    });
  } finally {
    isUploadingPhoto.value = false;
    // Reset file input to allow re-uploading the same file
    if (input) input.value = '';
  }
}

const formattedUserAddress = computed(() => {
  const address = user.value?.address;
  // const countryCode = user.value?.country_alpha2_code;

  const parts: string[] = [];

  if (address?.address_line_1) {
    parts.push(address.address_line_1);
  }
  if (address?.address_line_2) {
    parts.push(address.address_line_2);
  }

  const cityStringParts: string[] = [];
  if (address?.city?.postal_code) {
    cityStringParts.push(address.city.postal_code);
  }
  if (address?.city?.name) {
    cityStringParts.push(address.city.name);
  }
  if (cityStringParts.length > 0) {
    parts.push(cityStringParts.join(' '));
  }

  // if (countryCode) {
  //   parts.push(countryCode);
  // }

  if (parts.length === 0) {
    return t('profile.profile.profileTab.personnalData.addressNotSet');
  }

  return parts.join(', ');
});

const { loadCountries, getCountryByCode } = useCountries();

const userCountry = computed(() => getCountryByCode(user.value?.country_alpha2_code));

onMounted(async () => {
  if (!userStore.user) {
    isLoadingProfile.value = true;
    await userStore.initUser();
    isLoadingProfile.value = false;
  }
  await loadCountries();
});

const showEditProfileDialog = ref(false);

// Delete account state
const showDeleteAccountModal = ref(false);
</script>

<template>
  <div class="flex flex-col gap-8">
    <!-- Language Selection Section -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.profileTab.language.title') }}</h3>
      <div class="flex flex-col items-end justify-center gap-2">
        <Dropdown v-model="selectedLanguage" class="w-full md:w-1/2" :options="languagesList" option-label="label" @change="updateLanguage">
          <template #value="slotProps">
            {{ slotProps.value.label }}
          </template>
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Dropdown>
      </div>
    </div>

    <!-- User Information Section -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-4 text-center md:text-left">{{ t('profile.profile.profileTab.personnalData.title') }}</h3>
      <div class="flex flex-col items-center justify-center gap-2 md:items-start">
        <div class="flex justify-center md:justify-start">
          <div
            class="relative flex aspect-square h-24 max-h-24 w-24 max-w-24 cursor-pointer items-center justify-center rounded-full border-2 border-primary-200 object-cover p-1"
            :class="{ 'pointer-events-none opacity-50': isUploadingPhoto }"
            @click="fileInput?.click()"
          >
            <!-- Loading spinner -->
            <div v-if="isUploadingPhoto" class="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-30">
              <i class="pi pi-spin pi-spinner text-2xl text-white"></i>
            </div>
            <img
v-if="profileImageSrc" :src="profileImageSrc"
              alt="Profile picture"
              class="absolute inset-0 h-full w-full rounded-full object-cover p-1"
            />
            <ImagePlaceholderIcon v-else class="h-8 w-8 text-primary-950" />
          </div>
          <input
            id="profile-photo"
            ref="fileInput"
            type="file"
            name="profile-photo"
            accept="image/jpeg, image/jpg, image/png, image/webp, image.heic"
            class="mb-4 hidden"
            @change="uploadProfilePhoto"
          />
        </div>
        <div class="flex flex-col items-center justify-center md:items-start">
          <h3>{{ user?.first_name }} {{ user?.last_name }}</h3>
          <p v-if="user?.active_restaurant" class="text-xs">
            {{ t(`auth.user.roles.${user.active_restaurant.role}`) }}
          </p>
        </div>
      </div>
      <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h4 class="mt-2 font-semibold">{{ t('auth.user.form.label.phone_number') }}</h4>
          <p class="text-sm">{{ user?.phone_number || '-' }}</p>
        </div>
        <div>
          <h4 class="mt-2 font-semibold">{{ t('auth.user.form.label.oasi_number') }}</h4>
          <p class="text-sm">{{ user?.oasi_number || '-' }}</p>
        </div>
        <div>
          <h4 class="mt-2 font-semibold">{{ t('profile.suppliers.form.addressLabel') }}</h4>
          <div class="flex flex-col">
            <p
              :class="{
                'text-sm': true,
                'italic text-gray-500': formattedUserAddress === t('profile.profile.profileTab.personnalData.addressNotSet'),
              }"
            >
              {{ formattedUserAddress }}
            </p>
            <p v-if="userCountry" class="text-sm">{{ userCountry.flag_emoji }} {{ userCountry.name }}</p>
            <p v-else class="text-sm italic text-gray-500">-</p>
          </div>
        </div>
      </div>
      <Button class="mt-6 w-full md:w-auto" @click="showEditProfileDialog = true">{{ t('common.edit') }}</Button>
    </div>

    <!-- Password and Email Section -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.profileTab.passwordAndEmail.title') }}</h3>
      <div>
        <h4 class="mt-2 font-semibold">{{ t('profile.profile.profileTab.passwordAndEmail.email') }}</h4>
        <p class="text-sm">{{ user?.email }}</p>
      </div>
      <div class="mt-4">
        <!-- <h4 class="mt-2 font-semibold">{{ t('profile.profile.profileTab.passwordAndEmail.password') }}</h4> -->
        <Button outlined class="mt-1" @click="openChangePasswordModal = true">
          {{
            user?.password_set
              ? t('profile.profile.profileTab.passwordAndEmail.changePassword')
              : t('profile.profile.profileTab.passwordAndEmail.setPassword')
          }}
        </Button>
      </div>
    </div>

    <!-- Day Off Section -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.profileTab.dayOff.title') }}</h3>
      <TheDayOffSection :current-user="user" :loading="isLoadingProfile" />
    </div>

    <!-- Logout Section -->
    <div class="rounded-lg border border-red-500 p-4">
      <h3 class="mb-2 text-red-700">{{ t('profile.profile.profileTab.logoutSection.title') }}</h3>
      <Button :label="t('auth.logout')" class="mt-2 w-full" :loading="isFetching" severity="danger" outlined @click="logout" />
    </div>

    <!-- Delete Account Section -->
    <div class="rounded-lg border border-red-600 bg-red-50 p-4">
      <h3 class="mb-2 font-semibold text-red-700">{{ t('profile.profile.delete_account.title') }}</h3>
      <p class="mb-4 text-sm text-gray-700">{{ t('profile.profile.delete_account.description') }}</p>

      <div class="mb-4 rounded-lg border border-red-300 bg-red-100 p-3">
        <p class="mb-1 font-semibold text-sm text-red-800">{{ t('profile.profile.delete_account.warning_title') }}</p>
        <p class="text-sm text-red-700">{{ t('profile.profile.delete_account.warning_message') }}</p>
      </div>

      <Button :label="t('profile.profile.delete_account.confirm_deletion')" class="w-full" severity="danger" @click="showDeleteAccountModal = true" />
    </div>

    <Dialog
      v-model:visible="openChangePasswordModal"
      modal
      :header="
        user?.password_set
          ? t('profile.profile.profileTab.passwordAndEmail.changePassword')
          : t('profile.profile.profileTab.passwordAndEmail.setPassword')
      "
      :style="{ width: '25rem', maxWidth: '90vw' }"
    >
      <form class="flex flex-col gap-4" @submit.prevent="submitPasswordChange">
        <div class="mb-3 rounded border border-primary-400 bg-primary-100 p-3 text-primary-700">
          <p>
            <i class="pi pi-info-circle mr-2"></i>
            {{ t('profile.profile.profileTab.passwordAndEmail.logoutNotice') }}
          </p>
        </div>
        <div v-if="user?.password_set" class="flex flex-col gap-2">
          <label for="currentPassword">{{ t('auth.user.form.label.current_password') }}</label>
          <Password id="currentPassword" v-model="currentPassword" class="w-full" toggle-mask @blur="touchedPasswordFields.currentPassword = true" />
        </div>
        <div class="flex flex-col gap-2">
          <label for="newPassword">{{ t('auth.user.form.label.password') }}</label>
          <Password id="newPassword" v-model="newPassword" class="w-full" toggle-mask @blur="touchedPasswordFields.newPassword = true" />
          <InlineMessage v-if="!isNewPasswordValid" severity="error">
            {{ t('auth.user.form.error.invalid_password') }}
          </InlineMessage>
          <InlineMessage v-if="isSamePassword" severity="warn">
            {{ t('auth.user.form.error.same_password') || 'New password must be different from current one' }}
          </InlineMessage>
        </div>
        <div class="flex flex-col gap-2">
          <label for="confirmNewPassword">{{ t('auth.user.form.label.password_confirmation') }}</label>
          <Password
            id="confirmNewPassword"
            v-model="confirmNewPassword"
            class="w-full"
            toggle-mask
            @blur="touchedPasswordFields.confirmNewPassword = true"
          />
          <InlineMessage v-if="!isConfirmNewPasswordValid" severity="error">{{
            t('auth.user.form.error.invalid_password_confirmation')
          }}</InlineMessage>
        </div>
        <Button
          :label="user?.password_set ? t('common.edit') : t('common.create')"
          type="submit"
          :loading="isFetchingPassword"
          :disabled="!isFormValid"
        />
      </form>
    </Dialog>
    <Dialog v-model:visible="showEditProfileDialog" modal :header="t('common.edit')" :style="{ width: '40rem', maxWidth: '90vw' }">
      <EditProfile @close="showEditProfileDialog = false" />
    </Dialog>

    <!-- Delete Account Modal -->
    <DeleteAccountDialog v-model:visible="showDeleteAccountModal" />
  </div>
</template>
